import usePrivateLink from "../config/usePrivateLink";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Header from "./HostelHeader";
import Nav from "../master/NavBar.jsx";
import Footer from "../main/footer";
import { ToastMain, SweetAlertShow, LoadingDiv } from "../master/Master.jsx";

import {
  GetHostelList,
  HostelFeeGeneratePreview,
  HostelFeeGenerate,
  HostelStudentFeeSummary,
  HostelStudentFeeLedger,
} from "../../api/HostelService.js";

import { studentFeeDepositV4, studentFeeReceipt } from "../../api/Student";

import { BankList } from "../../api/BankService";

import HostelFeeReceipt from "../components/HostelFeeReceipt";
import { printElement } from "../master/Print";
import { Link } from "react-router-dom";
import WelcomeBar from "./WelcomeBar.jsx";

export default function FeeDeposit() {
  const hasAccess = usePrivateLink();

  // =========================================================
  // COMMON STATE
  // =========================================================

  const [loading, setLoading] = useState(false);

  const [hostels, setHostels] = useState("");
  const [hostelId, setHostel_id] = useState(localStorage.getItem("hostel_id"));
  const [hostelName, setHostelName] = useState("");

  // =========================================================
  // SUMMARY STATE
  // =========================================================

  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);

  // =========================================================
  // VIEW STATE
  // summary = student list
  // ledger  = student ledger/payment screen
  // =========================================================

  const [currentView, setCurrentView] = useState("summary");

  // =========================================================
  // SELECTED STUDENT / LEDGER
  // =========================================================

  const [selectedStudent, setSelectedStudent] = useState(null);

  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);

  // Currently selected fee row from ledger
  const [selectedFee, setSelectedFee] = useState(null);

  // =========================================================
  // GENERATE FEE MODAL
  // =========================================================

  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const [genHostelId, setGenHostelId] = useState("");
  const [genMonth, setGenMonth] = useState("");

  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [previewData, setPreviewData] = useState(null);

  const [rows, setRows] = useState([]);

  // =========================================================
  // PAYMENT STATE
  // =========================================================

  const [payAmount, setPayAmount] = useState("");
  const [payBankId, setPayBankId] = useState("");
  const [payTransactionNo, setPayTransactionNo] = useState("");
  const [payRemark, setPayRemark] = useState("");
  const [payDate, setPayDate] = useState("");

  const [banks, setBanks] = useState([]);
  const [paying, setPaying] = useState(false);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState({});
  const [receiptLoading, setReceiptLoading] = useState(false);

  // =========================================================
  // HELPERS
  // =========================================================

  const getTodayDate = () => {
    const date = new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getErrorMessage = (error) => {
    return (
      error?.response?.data?.message ??
      error?.response?.message ??
      error?.message ??
      "Something went wrong."
    );
  };

  // =========================================================
  // PRINT / EXPORT HELPERS
  // =========================================================

  const escapeHtml = (value) => {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const formatAmount = (value) => {
    return Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const downloadCsv = (filename, headers, data) => {
    const csvEscape = (value) => {
      const stringValue = String(value ?? "");
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const csv = [
      headers.map(csvEscape).join(","),
      ...data.map((row) => row.map(csvEscape).join(",")),
    ].join("\r\n");

    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const openPrintWindow = (title, content) => {
    const printWindow = window.open("", "_blank", "width=1200,height=800");

    if (!printWindow) {
      SweetAlertShow(
        "warning",
        "Popup Blocked",
        "Please allow pop-ups for this site to print.",
      );
      return;
    }

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(title)}</title>

        <meta charset="UTF-8" />

        <style>
          * {
            box-sizing: border-box;
          }

          body {
            font-family: Arial, Helvetica, sans-serif;
            margin: 0;
            padding: 30px;
            color: #222;
            background: #fff;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 20px;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 2px solid #222;
          }

          .print-header h1 {
            margin: 0 0 6px;
            font-size: 22px;
          }

          .print-header p {
            margin: 3px 0;
            color: #555;
            font-size: 13px;
          }

          .student-info {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
            margin-bottom: 20px;
          }

          .info-box {
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 10px 12px;
          }

          .info-box .label {
            display: block;
            color: #666;
            font-size: 11px;
            margin-bottom: 4px;
            text-transform: uppercase;
          }

          .info-box .value {
            font-weight: 600;
            font-size: 13px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
          }

          th,
          td {
            border: 1px solid #d8d8d8;
            padding: 8px 10px;
            font-size: 12px;
          }

          th {
            background: #f2f2f2;
            font-weight: 700;
            text-align: center;
          }

          td.text-center {
            text-align: center;
          }

          td.text-end {
            text-align: right;
          }

          .totals {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
            margin-top: 18px;
          }

          .total-box {
            border: 1px solid #ddd;
            padding: 12px;
            border-radius: 6px;
          }

          .total-box .label {
            display: block;
            color: #666;
            font-size: 11px;
            margin-bottom: 5px;
          }

          .total-box .value {
            font-size: 16px;
            font-weight: 700;
          }

          .footer {
            margin-top: 30px;
            color: #777;
            font-size: 10px;
            text-align: right;
          }

          @media print {
            body {
              padding: 12px;
            }

            .no-print {
              display: none !important;
            }

            thead {
              display: table-header-group;
            }

            tr {
              page-break-inside: avoid;
            }
          }

          @page {
            size: A4 landscape;
            margin: 10mm;
          }
        </style>
      </head>

      <body>
        ${content}

        <div class="footer">
          Printed on ${escapeHtml(new Date().toLocaleString("en-IN"))}
        </div>
      </body>
    </html>
  `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();

      setTimeout(() => {
        printWindow.close();
      }, 500);
    }, 300);
  };

  // =========================================================
  // STUDENT LIST PRINT
  // =========================================================

  const printStudentList = () => {
    if (!students?.length) {
      SweetAlertShow("info", "Nothing to Print", "No students are available.");
      return;
    }

    const rowsHtml = students
      .map(
        (item, index) => `
        <tr>
          <td class="text-center">
            ${index + 1}
          </td>

          <td>
            ${escapeHtml(item.student_name)}
          </td>

          <td class="text-center">
            ${escapeHtml(item.mobile)}
          </td>

          <td class="text-center">
            ${escapeHtml(item.registration_no)}
          </td>

          <td class="text-end">
            ₹${formatAmount(item.total_paid_amount)}
          </td>

          <td class="text-end">
            ₹${formatAmount(item.total_left_amount)}
          </td>

          <td class="text-center">
            ${escapeHtml(item.pending_months_count)} /
            ${escapeHtml(item.total_months_count)}
          </td>
        </tr>
      `,
      )
      .join("");

    const content = `
    <div class="print-header">
      <div>
        <h1>Hostel Fee Student List</h1>

        <p>
          <strong>Hostel:</strong>
          ${escapeHtml(hostelName || "-")}
        </p>

        <p>
          <strong>Search:</strong>
          ${escapeHtml(search || "All Students")}
        </p>

        <p>
          <strong>Filter:</strong>
          ${onlyPending ? "Only students with pending fee" : "All students"}
        </p>
      </div>

      <div>
        <p>
          <strong>Total Students:</strong>
          ${students.length}
        </p>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Student</th>
          <th>Mobile</th>
          <th>Registration No</th>
          <th>Paid</th>
          <th>Pending</th>
          <th>Months Pending</th>
        </tr>
      </thead>

      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  `;

    openPrintWindow("Hostel Fee Student List", content);
  };

  // =========================================================
  // STUDENT LIST EXPORT
  // =========================================================

  const exportStudentList = () => {
    if (!students?.length) {
      SweetAlertShow("info", "Nothing to Export", "No students are available.");
      return;
    }

    const data = students.map((item, index) => [
      index + 1,
      item.student_name,
      item.mobile,
      item.registration_no,
      Number(item.total_paid_amount || 0),
      Number(item.total_left_amount || 0),
      `${item.pending_months_count || 0} / ${item.total_months_count || 0}`,
    ]);

    const safeHostelName = String(hostelName || "Hostel")
      .replace(/[^a-z0-9]+/gi, "_")
      .replace(/^_+|_+$/g, "");

    downloadCsv(
      `Hostel_Student_List_${safeHostelName || "Hostel"}.csv`,
      [
        "S.No.",
        "Student",
        "Mobile",
        "Registration No",
        "Paid Amount",
        "Pending Amount",
        "Months Pending",
      ],
      data,
    );
  };

  // =========================================================
  // COMPLETE LEDGER PRINT
  // =========================================================

  const printLedger = () => {
    if (!ledgerData?.fees?.length) {
      SweetAlertShow(
        "info",
        "Nothing to Print",
        "No fee records are available for this student.",
      );
      return;
    }

    const student = ledgerData?.student || {};
    const totals = ledgerData?.totals || {};

    const rowsHtml = ledgerData.fees
      .map((fee, index) => {
        const pending = Number(fee.left_amount || 0) > 0;

        return `
        <tr>
          <td class="text-center">
            ${index + 1}
          </td>

          <td>
            ${escapeHtml(fee.month)}
          </td>

          <td class="text-end">
            ₹${formatAmount(fee.final_amount)}
          </td>

          <td class="text-end">
            ₹${formatAmount(fee.paid_amount)}
          </td>

          <td class="text-end">
            ₹${formatAmount(fee.left_amount)}
          </td>

          <td class="text-center">
            ${pending ? "Pending" : "Paid"}
          </td>
        </tr>
      `;
      })
      .join("");

    const content = `
    <div class="print-header">
      <div>
        <h1>Hostel Fee Ledger</h1>

        <p>
          <strong>Student:</strong>
          ${escapeHtml(student.student_name || "-")}
        </p>
      </div>

      <div>
        <p>
          <strong>Registration No:</strong>
          ${escapeHtml(student.registration_no || "-")}
        </p>

        <p>
          <strong>Mobile:</strong>
          ${escapeHtml(student.mobile || "-")}
        </p>
      </div>
    </div>

    <div class="student-info">

      <div class="info-box">
        <span class="label">
          Hostel
        </span>

        <span class="value">
          ${escapeHtml(student.hostel_name || "-")}
        </span>
      </div>

      <div class="info-box">
        <span class="label">
          Room
        </span>

        <span class="value">
          ${escapeHtml(student.room_no || "-")}
        </span>
      </div>

      <div class="info-box">
        <span class="label">
          Bed
        </span>

        <span class="value">
          ${escapeHtml(student.bed_no || "-")}
        </span>
      </div>

      <div class="info-box">
        <span class="label">
          Total Records
        </span>

        <span class="value">
          ${ledgerData.fees.length}
        </span>
      </div>

    </div>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Month</th>
          <th>Final Amount</th>
          <th>Paid Amount</th>
          <th>Pending Amount</th>
          <th>Status</th>
        </tr>
      </thead>

      <tbody>
        ${rowsHtml}
      </tbody>
    </table>

    <div class="totals">

      <div class="total-box">
        <span class="label">
          Total Fee
        </span>

        <span class="value">
          ₹${formatAmount(totals.total_final_amount)}
        </span>
      </div>

      <div class="total-box">
        <span class="label">
          Total Paid
        </span>

        <span class="value">
          ₹${formatAmount(totals.total_paid_amount)}
        </span>
      </div>

      <div class="total-box">
        <span class="label">
          Total Pending
        </span>

        <span class="value">
          ₹${formatAmount(totals.total_left_amount)}
        </span>
      </div>

    </div>
  `;

    openPrintWindow(
      `Fee Ledger - ${student.student_name || "Student"}`,
      content,
    );
  };

  // =========================================================
  // COMPLETE LEDGER EXPORT
  // =========================================================

  const exportLedger = () => {
    if (!ledgerData?.fees?.length) {
      SweetAlertShow(
        "info",
        "Nothing to Export",
        "No fee records are available for this student.",
      );
      return;
    }

    const student = ledgerData?.student || {};
    const totals = ledgerData?.totals || {};

    const data = ledgerData.fees.map((fee, index) => [
      index + 1,
      fee.month,
      Number(fee.final_amount || 0),
      Number(fee.paid_amount || 0),
      Number(fee.left_amount || 0),
      Number(fee.left_amount || 0) > 0 ? "Pending" : "Paid",
    ]);

    data.push([]);

    data.push([
      "",
      "TOTAL",
      Number(totals.total_final_amount || 0),
      Number(totals.total_paid_amount || 0),
      Number(totals.total_left_amount || 0),
      "",
    ]);

    const safeStudentName = String(student.student_name || "Student")
      .replace(/[^a-z0-9]+/gi, "_")
      .replace(/^_+|_+$/g, "");

    downloadCsv(
      `Fee_Ledger_${safeStudentName || "Student"}.csv`,
      [
        "S.No.",
        "Month",
        "Final Amount",
        "Paid Amount",
        "Pending Amount",
        "Status",
      ],
      data,
    );
  };

  // =========================================================
  // HOSTEL
  // =========================================================

  const getHostel = async () => {
    setLoading(true);

    let addUrl = "";

    if (hostelId) {
      addUrl += `&hostel_id=${hostelId}`;
    }

    try {
      const response = await GetHostelList(addUrl);

      if (response.data.status) {
        setHostels(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // SUMMARY
  // =========================================================

  const getSummary = async () => {
    if (!hostelId) {
      setStudents([]);
      setHostelName("");
      return;
    }

    setLoading(true);

    let addUrl = `hostel_id=${hostelId}`;

    if (search) {
      addUrl += `&search=${encodeURIComponent(search)}`;
    }

    if (onlyPending) {
      addUrl += `&only_pending=1`;
    }

    try {
      const response = await HostelStudentFeeSummary(addUrl);

      if (response.data.status) {
        setHostelName(response?.data?.data?.hostel_name || "");

        setStudents(response?.data?.data?.students || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHostel();
  }, []);

  useEffect(() => {
    if (currentView === "summary") {
      getSummary();
    }
  }, [hostelId, search, onlyPending, currentView]);

  // =========================================================
  // STORAGE CHANGE
  // =========================================================

  useEffect(() => {
    const handleStorageChange = () => {
      const newHostelId = localStorage.getItem("hostel_id");

      setHostel_id(newHostelId);

      // If hostel changes, return to summary
      setCurrentView("summary");
      setSelectedStudent(null);
      setLedgerData(null);
      setSelectedFee(null);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // =========================================================
  // LOAD LEDGER
  // =========================================================

  const loadLedger = async (student) => {
    if (!student?.hostel_student_id) {
      SweetAlertShow("error", "Error", "Hostel student ID is missing.");

      return null;
    }

    setLedgerLoading(true);

    try {
      const response = await HostelStudentFeeLedger(student.hostel_student_id);

      if (response.data.status) {
        const data = response?.data?.data;

        /*
         * The summary API already gives us both:
         *
         * hostel_student_id
         * student_id
         *
         * Therefore we preserve those IDs even if the ledger API
         * does not return them.
         */

        const normalizedData = {
          ...data,

          student: {
            ...(data?.student || {}),

            hostel_student_id:
              data?.student?.hostel_student_id ?? student.hostel_student_id,

            student_id: data?.student?.student_id ?? student.student_id,

            student_name: data?.student?.student_name ?? student.student_name,

            registration_no:
              data?.student?.registration_no ?? student.registration_no,

            mobile: data?.student?.mobile ?? student.mobile,
          },

          fees: Array.isArray(data?.fees) ? data.fees : [],

          totals: {
            total_final_amount: data?.totals?.total_final_amount ?? 0,

            total_paid_amount: data?.totals?.total_paid_amount ?? 0,

            total_left_amount: data?.totals?.total_left_amount ?? 0,
          },
        };

        setLedgerData(normalizedData);

        return normalizedData;
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setLedgerLoading(false);
    }

    return null;
  };

  // =========================================================
  // OPEN LEDGER SCREEN
  // =========================================================

  const openLedger = async (student) => {
    /*
     * Store the complete student object.
     *
     * This means payment never has to depend on a modal's
     * ledgerData to find student_id / hostel_student_id.
     */
    setSelectedStudent(student);

    setSelectedFee(null);

    setPayAmount("");
    setPayBankId("");
    setPayTransactionNo("");
    setPayRemark("");
    setPayDate(getTodayDate());

    setCurrentView("ledger");

    await loadLedger(student);
  };

  // =========================================================
  // BACK TO SUMMARY
  // =========================================================

  const backToSummary = async () => {
    setCurrentView("summary");

    setSelectedStudent(null);
    setLedgerData(null);
    setSelectedFee(null);

    setPayAmount("");
    setPayBankId("");
    setPayTransactionNo("");
    setPayRemark("");
    setPayDate("");
  };

  // =========================================================
  // SELECT FEE
  // =========================================================

  const selectFee = (fee) => {
    if (!fee) return;

    const leftAmount = Number(fee.left_amount || 0);

    /*
     * Do not allow selecting already paid fees.
     */
    if (leftAmount <= 0 || fee.is_paid) {
      return;
    }

    setSelectedFee(fee);

    /*
     * Automatically fill pending amount.
     */
    setPayAmount(fee.left_amount);

    setPayBankId("");
    setPayTransactionNo("");
    setPayRemark("");
    setPayDate(getTodayDate());

    /*
     * Load banks if they haven't been loaded.
     */
    if (banks.length === 0) {
      fetchBanks();
    }
  };

  // =========================================================
  // BANKS
  // =========================================================

  const fetchBanks = async () => {
    try {
      const response = await BankList();

      if (response.data.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    }
  };

  // =========================================================
  // HOSTEL FEE RECEIPT
  // =========================================================

  const handlePrintReceipt = async (id) => {
    try {
      if (!id) {
        SweetAlertShow(
          "error",
          "Receipt Error",
          "Payment receipt ID is missing.",
        );
        return;
      }

      setReceiptLoading(true);
      setShowReceiptModal(true);

      const response = await studentFeeReceipt(id);

      if (response?.data?.status) {
        setReceiptData(response.data.data);
      } else {
        setShowReceiptModal(false);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Receipt data not found",
        );
      }
    } catch (error) {
      console.error("Hostel receipt error:", error);

      setShowReceiptModal(false);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load receipt",
      );
    } finally {
      setReceiptLoading(false);
    }
  };

  const closeReceiptModal = () => {
    setShowReceiptModal(false);
    setReceiptData({});
  };

  // =========================================================
  // PAYMENT
  // =========================================================

  const submitPay = async () => {
    if (!selectedStudent) {
      SweetAlertShow("error", "Error", "Student information is missing.");
      return;
    }

    if (!selectedFee) {
      SweetAlertShow(
        "info",
        "Warning",
        "Please select a pending fee from the ledger.",
      );
      return;
    }

    const amount = Number(payAmount || 0);

    const leftAmount = Number(selectedFee?.left_amount || 0);

    // -------------------------------------------------------
    // VALIDATIONS
    // -------------------------------------------------------

    if (!amount || amount <= 0) {
      SweetAlertShow("info", "Warning", "Enter a valid amount.");
      return;
    }

    if (amount > leftAmount) {
      SweetAlertShow(
        "info",
        "Warning",
        `Amount cannot exceed the pending amount for this month (₹${leftAmount}).`,
      );
      return;
    }

    if (!payBankId) {
      SweetAlertShow("info", "Warning", "Select a bank.");
      return;
    }

    if (!payDate) {
      SweetAlertShow("info", "Warning", "Select a payment date.");
      return;
    }

    if (!selectedStudent.hostel_student_id) {
      SweetAlertShow("error", "Error", "Hostel student ID is missing.");
      return;
    }

    if (!selectedStudent.student_id) {
      SweetAlertShow("error", "Error", "Student ID is missing.");
      return;
    }

    if (!selectedFee.id) {
      SweetAlertShow("error", "Error", "Hostel fee ID is missing.");
      return;
    }
    setPaying(true);

    try {
      const fd = new FormData();

      // -----------------------------------------------------
      // STUDENT INFORMATION
      // -----------------------------------------------------

      fd.append("student_id", String(selectedStudent.student_id));

      fd.append("hostel_student_id", String(selectedStudent.hostel_student_id));

      // -----------------------------------------------------
      // PAYMENT INFORMATION
      // -----------------------------------------------------

      fd.append("amount", String(amount));

      fd.append("bank_id", String(payBankId));

      fd.append("transaction_no", payTransactionNo || "");

      fd.append("remark", payRemark || "");

      fd.append("payment_date", payDate);

      // -----------------------------------------------------
      // MODULE
      // -----------------------------------------------------

      fd.append("module[0][type]", "Hostel Fee");

      fd.append("module[0][ids]", String(selectedFee.id));

      fd.append("module[0][amount]", String(amount));

      // -----------------------------------------------------
      // DEBUG
      // -----------------------------------------------------

      console.log("========== HOSTEL PAYMENT REQUEST ==========");

      for (const [key, value] of fd.entries()) {
        console.log(`${key}:`, value);
      }

      console.log("============================================");

      // -----------------------------------------------------
      // API
      // -----------------------------------------------------

      const response = await studentFeeDepositV4(fd);

      if (response.data.status) {
        ToastMain.fire({
          icon: "success",
          title: response.data.message || "Fee paid successfully.",
        });

        const paymentId = response?.data?.data?.id;

        /*
         * Clear payment selection.
         */
        setSelectedFee(null);

        setPayAmount("");
        setPayBankId("");
        setPayTransactionNo("");
        setPayRemark("");
        setPayDate(getTodayDate());

        /*
         * Refresh the ledger.
         *
         * This updates:
         * - paid amount
         * - left amount
         * - status
         * - totals
         */
        await loadLedger(selectedStudent);

        /*
         * Refresh summary in background.
         */
        await getSummary();
        if (paymentId) {
          await handlePrintReceipt(paymentId);
        } else {
          SweetAlertShow(
            "warning",
            "Receipt Error",
            "Payment was successful, but receipt ID was not returned.",
          );
        }
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      console.error("Hostel fee payment error:", error);

      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setPaying(false);
    }
  };

  // =========================================================
  // GENERATE FEE
  // =========================================================

  const openGenerateModal = () => {
    setGenHostelId(hostelId || "");
    setGenMonth("");
    setPreviewData(null);
    setRows([]);

    setShowGenerateModal(true);
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    setPreviewData(null);
    setRows([]);
  };

  const fetchPreview = async () => {
    if (!genHostelId || !genMonth) {
      SweetAlertShow("info", "Warning", "Please select hostel and month.");
      return;
    }

    setPreviewLoading(true);
    setPreviewData(null);
    setRows([]);

    try {
      const response = await HostelFeeGeneratePreview({
        hostel_id: genHostelId,
        month: genMonth,
      });

      if (response.data.status) {
        const data = response?.data?.data;

        setPreviewData(data);

        setRows(
          (data?.pending || []).map((item) => ({
            hostel_student_id: item.hostel_student_id,

            student_name: item.student_name,

            registration_no: item.registration_no,

            total_amount: item.total_amount,

            amount: item.default_amount,

            is_prorated: item.is_prorated,

            checked: true,
          })),
        );
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleRowChecked = (id) => {
    setRows((prev) =>
      prev.map((row) =>
        row.hostel_student_id === id
          ? {
            ...row,
            checked: !row.checked,
          }
          : row,
      ),
    );
  };

  const toggleAllChecked = (checked) => {
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        checked,
      })),
    );
  };

  const updateRowAmount = (id, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.hostel_student_id === id
          ? {
            ...row,
            amount: value,
          }
          : row,
      ),
    );
  };

  const submitGenerate = async () => {
    const selected = rows.filter((row) => row.checked);

    if (selected.length === 0) {
      SweetAlertShow(
        "info",
        "Warning",
        "Select at least one student to generate fee for.",
      );
      return;
    }

    setGenerating(true);

    try {
      const response = await HostelFeeGenerate({
        hostel_id: genHostelId,
        month: genMonth,

        students: selected.map((row) => ({
          hostel_student_id: row.hostel_student_id,

          amount: row.amount,
        })),
      });

      if (response.data.status) {
        const { created = [], skipped = [] } = response?.data?.data || {};

        ToastMain.fire({
          icon: "success",

          title:
            `${created.length} fee record(s) generated` +
            (skipped.length ? `, ${skipped.length} skipped` : "") +
            ".",
        });

        closeGenerateModal();

        await getSummary();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setGenerating(false);
    }
  };

  const allChecked = rows.length > 0 && rows.every((row) => row.checked);

  const checkedCount = rows.filter((row) => row.checked).length;

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <WelcomeBar />

                {/* =================================================
                    SUMMARY VIEW
                ================================================= */}

                {currentView === "summary" && (
                  <>
                    {/* PAGE HEADER / FILTER */}
                    <div className="card my-5">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div>
                            <h4 className="text-primary mb-1">
                              Hostel Fee Deposit
                            </h4>

                            <small className="text-muted">
                              Manage hostel student fees and payments
                            </small>
                          </div>

                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {hasAccess("/hostel/fee/deposit/print") && (
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={printStudentList}
                                disabled={!students?.length}
                                title="Print student list"
                              >
                                <i className="ri-printer-line ri-20px me-1" />
                                Print
                              </button>
                            )}

                            {hasAccess("/hostel/fee/deposit/export") && (
                              <button
                                type="button"
                                className="btn btn-outline-success"
                                onClick={exportStudentList}
                                disabled={!students?.length}
                                title="Export student list"
                              >
                                <i className="ri-file-excel-2-line ri-20px me-1" />
                                Export
                              </button>
                            )}

                            {hasAccess("/hostel/fee/generate") && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={openGenerateModal}
                              >
                                <i className="ri-add-circle-line ri-20px me-1" />
                                Generate Fee
                              </button>
                            )}
                          </div>
                        </div>

                        <hr />

                        {!hostelId ? (
                          <div className="alert alert-warning mb-0">
                            Select a hostel first to view student fee summary.
                          </div>
                        ) : (
                          <div className="row gy-3 align-items-center">
                            <div className="col-md-5 mb-3">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Name / Mobile / Reg No"
                                  value={search}
                                  onChange={(e) => setSearch(e.target.value)}
                                />

                                <label>Name / Mobile / Reg No</label>
                              </div>
                            </div>

                            <div className="col-md-5 mb-3">
                              <div className="form-check form-switch mt-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="onlyPendingSwitch"
                                  checked={onlyPending}
                                  onChange={(e) =>
                                    setOnlyPending(e.target.checked)
                                  }
                                />

                                <label
                                  className="form-check-label"
                                  htmlFor="onlyPendingSwitch"
                                >
                                  Only students with pending fee
                                </label>
                              </div>
                            </div>

                            <div className="col-md-2 mb-3">
                              <button
                                type="button"
                                className="btn btn-label-danger"
                                onClick={() => {
                                  setSearch("");
                                  setOnlyPending(false);
                                }}
                                title="Reset filters"
                              >
                                <i className="ri-restart-line ri-20px" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* STUDENT SUMMARY */}
                    {hostelId && (
                      <div className="card my-5">
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table align-middle">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th className="text-start">Student</th>
                                  <th className="text-center">Mobile</th>
                                  <th className="text-center">Reg No</th>
                                  <th className="text-center">Paid</th>
                                  <th className="text-center">Pending</th>
                                  <th className="text-center">
                                    Months Pending
                                  </th>
                                  <th className="text-center">Action</th>
                                </tr>
                              </thead>

                              <tbody>
                                {students.length === 0 && (
                                  <tr>
                                    <td
                                      colSpan={8}
                                      className="text-center py-5"
                                    >
                                      <div className="text-muted">
                                        <i className="ri-user-search-line ri-32px d-block mb-2" />
                                        No students found.
                                      </div>
                                    </td>
                                  </tr>
                                )}

                                {students.map((item, index) => (
                                  <tr
                                    key={item.hostel_student_id}
                                    className="text-center"
                                  >
                                    <td>{index + 1}</td>

                                    <td className="text-start">
                                      <div className="fw-medium">
                                        {item.student_name}
                                      </div>
                                    </td>

                                    <td>{item.mobile}</td>

                                    <td>{item.registration_no}</td>

                                    <td>₹{item.total_paid_amount}</td>

                                    <td>
                                      {Number(item.total_left_amount) > 0 ? (
                                        <span className="badge bg-label-danger">
                                          ₹{item.total_left_amount}
                                        </span>
                                      ) : (
                                        <span className="badge bg-label-success">
                                          Cleared
                                        </span>
                                      )}
                                    </td>

                                    <td>
                                      {item.pending_months_count} /{" "}
                                      {item.total_months_count}
                                    </td>

                                    <td>
                                      <div className="d-flex gap-2 justify-content-center">
                                        {/* LEDGER */}
                                        {hasAccess("  ") && (
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => openLedger(item)}
                                        >
                                          <i className="ri-file-list-3-line ri-16px me-1" />
                                          Ledger
                                        </button>
                                        )}

                                        {/* OLD PAY & RECEIPT PAGE */}
                                        {hasAccess("/student/fees/deposit") && (
                                          <Link
                                            to={`/student/fees/deposit?id=${item.hostel_student_id}&student_id=${item.student_id}&hostel_id=${hostelId}`}
                                            className="btn btn-outline-primary btn-sm"
                                          >
                                            Pay & Receipt
                                          </Link>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* =================================================
                    LEDGER VIEW
                ================================================= */}

                {currentView === "ledger" && (
                  <>
                    {/* BACK BUTTON + TITLE */}
                    <div className="card my-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <button
                              type="button"
                              className="btn btn-label-secondary"
                              onClick={backToSummary}
                            >
                              <i className="ri-arrow-left-line ri-20px me-1" />
                              Back
                            </button>

                            <div>
                              <h4 className="mb-1 text-primary">Fee Ledger</h4>

                              <div className="text-muted">
                                {selectedStudent?.student_name}
                              </div>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={printLedger}
                              disabled={
                                ledgerLoading || !ledgerData?.fees?.length
                              }
                              title="Print complete ledger"
                            >
                              <i className="ri-printer-line ri-20px me-1" />
                              Print
                            </button>

                            <button
                              type="button"
                              className="btn btn-outline-success"
                              onClick={exportLedger}
                              disabled={
                                ledgerLoading || !ledgerData?.fees?.length
                              }
                              title="Export complete ledger"
                            >
                              <i className="ri-file-excel-2-line ri-20px me-1" />
                              Export
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* LEDGER LOADING */}
                    {ledgerLoading && (
                      <div className="card mb-4">
                        <div className="card-body text-center py-5">
                          <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                          />

                          <div className="text-muted">
                            Loading fee ledger...
                          </div>
                        </div>
                      </div>
                    )}

                    {!ledgerLoading && ledgerData && (
                      <>
                        {/* =========================================
                              STUDENT INFORMATION
                          ========================================= */}

                        <div className="card mb-4">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                              <div>
                                <h4 className="mb-2">
                                  {ledgerData?.student?.student_name}
                                </h4>

                                <div className="d-flex flex-wrap gap-2">
                                  <span className="badge bg-label-secondary">
                                    <i className="ri-profile-line me-1" />
                                    Reg No:{" "}
                                    {ledgerData?.student?.registration_no}
                                  </span>

                                  <span className="badge bg-label-secondary">
                                    <i className="ri-phone-line me-1" />
                                    Mobile: {ledgerData?.student?.mobile}
                                  </span>

                                  {ledgerData?.student?.hostel_name && (
                                    <span className="badge bg-label-primary">
                                      <i className="ri-building-2-line me-1" />
                                      {ledgerData.student.hostel_name}
                                    </span>
                                  )}

                                  {(ledgerData?.student?.room_no ||
                                    ledgerData?.student?.bed_no) && (
                                      <span className="badge bg-label-primary">
                                        <i className="ri-hotel-bed-line me-1" />
                                        Room {ledgerData.student.room_no} / Bed{" "}
                                        {ledgerData.student.bed_no}
                                      </span>
                                    )}
                                </div>
                              </div>

                              {/* TOTAL PENDING */}
                              <div className="text-end">
                                <small className="text-muted d-block mb-1">
                                  Total Pending
                                </small>

                                <h3 className="text-danger mb-0">
                                  ₹{ledgerData?.totals?.total_left_amount}
                                </h3>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* =========================================
                              MAIN TWO COLUMN AREA
                          ========================================= */}

                        <div className="row g-4">
                          {/* =======================================
                                LEFT: LEDGER
                            ======================================= */}

                          <div className="col-lg-7">
                            <div className="card h-100">
                              <div className="card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h5 className="mb-1">Fee Ledger</h5>

                                    <small className="text-muted">
                                      Select a pending month to make payment
                                    </small>
                                  </div>

                                  <span className="badge bg-label-primary">
                                    {ledgerData?.fees?.length || 0} Records
                                  </span>
                                </div>
                              </div>

                              {/* IMPORTANT:
                                    Only this section scrolls
                                */}
                              <div
                                className="card-body p-0"
                                style={{
                                  maxHeight: "calc(100vh - 390px)",
                                  minHeight: "420px",
                                  overflowY: "auto",
                                }}
                              >
                                {ledgerData?.fees?.length === 0 ? (
                                  <div className="text-center py-5 px-3">
                                    <i className="ri-file-list-3-line ri-40px text-muted d-block mb-3" />

                                    <h6>No fee records generated</h6>

                                    <p className="text-muted mb-0">
                                      No hostel fee has been generated for this
                                      student yet.
                                    </p>
                                  </div>
                                ) : (
                                  <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                      <thead
                                        className="table-light"
                                        style={{
                                          position: "sticky",
                                          top: 0,
                                          zIndex: 2,
                                        }}
                                      >
                                        <tr className="text-center">
                                          <th>Month</th>

                                          <th>Final</th>

                                          <th>Paid</th>

                                          <th>Pending</th>

                                          <th>Status</th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {ledgerData.fees.map((fee) => {
                                          const isPending =
                                            Number(fee.left_amount || 0) > 0;

                                          const isSelected =
                                            selectedFee?.id === fee.id;

                                          return (
                                            <tr
                                              key={fee.id}
                                              onClick={() =>
                                                isPending && selectFee(fee)
                                              }
                                              style={{
                                                cursor: isPending
                                                  ? "pointer"
                                                  : "default",

                                                backgroundColor: isSelected
                                                  ? "rgba(105, 108, 255, 0.08)"
                                                  : undefined,
                                              }}
                                            >
                                              <td className="text-center">
                                                <div className="fw-medium">
                                                  {fee.month}
                                                </div>

                                                {isSelected && (
                                                  <small className="text-primary">
                                                    Selected for payment
                                                  </small>
                                                )}
                                              </td>

                                              <td className="text-center">
                                                ₹{fee.final_amount}
                                              </td>

                                              <td className="text-center text-success">
                                                ₹{fee.paid_amount}
                                              </td>

                                              <td className="text-center">
                                                {isPending ? (
                                                  <span className="badge bg-label-danger">
                                                    ₹{fee.left_amount}
                                                  </span>
                                                ) : (
                                                  <span className="text-muted">
                                                    ₹0
                                                  </span>
                                                )}
                                              </td>

                                              <td className="text-center">
                                                {isPending ? (
                                                  <span className="badge bg-label-warning">
                                                    <i className="ri-time-line me-1" />
                                                    Pending
                                                  </span>
                                                ) : (
                                                  <span className="badge bg-label-success">
                                                    <i className="ri-check-line me-1" />
                                                    Paid
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>

                              {/* TOTALS */}
                              {ledgerData?.fees?.length > 0 && (
                                <div className="card-footer">
                                  <div className="row text-center g-3">
                                    <div className="col-4">
                                      <small className="text-muted d-block">
                                        Total
                                      </small>

                                      <strong>
                                        ₹
                                        {ledgerData?.totals?.total_final_amount}
                                      </strong>
                                    </div>

                                    <div className="col-4">
                                      <small className="text-muted d-block">
                                        Paid
                                      </small>

                                      <strong className="text-success">
                                        ₹{ledgerData?.totals?.total_paid_amount}
                                      </strong>
                                    </div>

                                    <div className="col-4">
                                      <small className="text-muted d-block">
                                        Pending
                                      </small>

                                      <strong className="text-danger">
                                        ₹{ledgerData?.totals?.total_left_amount}
                                      </strong>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* =======================================
                                RIGHT: PAYMENT
                            ======================================= */}

                          <div className="col-lg-5">
                            <div
                              className="card"
                              style={{
                                position: "sticky",
                                top: "90px",
                              }}
                            >
                              <div className="card-header">
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                      width: 42,
                                      height: 42,
                                      background: "rgba(105,108,255,.12)",
                                    }}
                                  >
                                    <i className="ri-bank-card-line ri-20px text-primary" />
                                  </div>

                                  <div>
                                    <h5 className="mb-1">Make Payment</h5>

                                    <small className="text-muted">
                                      Hostel Fee
                                    </small>
                                  </div>
                                </div>
                              </div>

                              <div className="card-body">
                                {!selectedFee ? (
                                  <div className="text-center py-5">
                                    <div
                                      className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                                      style={{
                                        width: 70,
                                        height: 70,
                                        background: "rgba(105,108,255,.10)",
                                      }}
                                    >
                                      <i className="ri-hand-coin-line ri-32px text-primary" />
                                    </div>

                                    <h6 className="mb-2">Select a Fee</h6>

                                    <p className="text-muted mb-0">
                                      Select any pending month from the ledger
                                      to start payment.
                                    </p>
                                  </div>
                                ) : (
                                  <>
                                    {/* SELECTED FEE */}
                                    <div
                                      className="rounded p-3 mb-4"
                                      style={{
                                        background: "rgba(105,108,255,.06)",
                                        border:
                                          "1px solid rgba(105,108,255,.15)",
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                          <small className="text-muted d-block">
                                            Paying For
                                          </small>

                                          <h5 className="mb-0">
                                            {selectedFee.month}
                                          </h5>
                                        </div>

                                        <div className="text-end">
                                          <small className="text-muted d-block">
                                            Pending
                                          </small>

                                          <h5 className="text-danger mb-0">
                                            ₹{selectedFee.left_amount}
                                          </h5>
                                        </div>
                                      </div>
                                    </div>

                                    {/* PAYMENT FORM */}
                                    <div className="row g-3">
                                      {/* AMOUNT */}
                                      <div className="col-12">
                                        <div className="form-floating form-floating-outline">
                                          <input
                                            type="number"
                                            className="form-control"
                                            min={0.01}
                                            max={selectedFee.left_amount}
                                            step="0.01"
                                            value={payAmount}
                                            onChange={(e) =>
                                              setPayAmount(e.target.value)
                                            }
                                          />

                                          <label>Amount</label>
                                        </div>

                                        <small className="text-muted">
                                          Maximum payable: ₹
                                          {selectedFee.left_amount}
                                        </small>
                                      </div>

                                      {/* BANK */}
                                      <div className="col-12">
                                        <div className="form-floating form-floating-outline">
                                          <select
                                            className="form-select"
                                            value={payBankId}
                                            onChange={(e) =>
                                              setPayBankId(e.target.value)
                                            }
                                          >
                                            <option value="">
                                              Select Bank
                                            </option>

                                            {banks?.map((bank) => (
                                              <option
                                                key={bank.id}
                                                value={bank.id}
                                              >
                                                {bank.account_no}
                                                {bank.owner_name
                                                  ? ` (${bank.owner_name})`
                                                  : ""}
                                              </option>
                                            ))}
                                          </select>

                                          <label>Bank</label>
                                        </div>
                                      </div>

                                      {/* PAYMENT DATE */}
                                      <div className="col-md-6">
                                        <div className="form-floating form-floating-outline">
                                          <input
                                            type="date"
                                            className="form-control"
                                            value={payDate}
                                            onChange={(e) =>
                                              setPayDate(e.target.value)
                                            }
                                          />

                                          <label>Payment Date</label>
                                        </div>
                                      </div>

                                      {/* TRANSACTION */}
                                      <div className="col-md-6">
                                        <div className="form-floating form-floating-outline">
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={payTransactionNo}
                                            onChange={(e) =>
                                              setPayTransactionNo(
                                                e.target.value,
                                              )
                                            }
                                          />

                                          <label>Transaction No</label>
                                        </div>
                                      </div>

                                      {/* REMARK */}
                                      <div className="col-12">
                                        <div className="form-floating form-floating-outline">
                                          <textarea
                                            className="form-control"
                                            style={{
                                              minHeight: "100px",
                                            }}
                                            value={payRemark}
                                            onChange={(e) =>
                                              setPayRemark(e.target.value)
                                            }
                                          />

                                          <label>Remark</label>
                                        </div>
                                      </div>
                                    </div>

                                    {/* PAYMENT BUTTON */}
                                    <div className="mt-4">
                                      <button
                                        type="button"
                                        className="btn btn-primary w-100"
                                        onClick={submitPay}
                                        disabled={paying}
                                      >
                                        {paying ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm me-2"
                                              role="status"
                                            />
                                            Processing Payment...
                                          </>
                                        ) : (
                                          <>
                                            <i className="ri-secure-payment-line ri-20px me-1" />
                                            Pay ₹{payAmount || 0}
                                          </>
                                        )}
                                      </button>
                                    </div>

                                    {/* CANCEL SELECTION */}
                                    <button
                                      type="button"
                                      className="btn btn-label-secondary w-100 mt-2"
                                      onClick={() => {
                                        setSelectedFee(null);

                                        setPayAmount("");
                                        setPayBankId("");
                                        setPayTransactionNo("");
                                        setPayRemark("");
                                      }}
                                      disabled={paying}
                                    >
                                      Clear Selection
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <Footer />
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
    GENERATE FEE MODAL
    Render through Portal so it is above Header/Nav
========================================================= */}

      {showGenerateModal &&
        createPortal(
          <>
            {/* BACKDROP */}
            <div
              className="hostel-fee-modal-backdrop"
              onClick={closeGenerateModal}
            />

            {/* MODAL */}
            <div
              className="hostel-fee-modal-wrapper"
              role="dialog"
              aria-modal="true"
              aria-labelledby="generateHostelFeeTitle"
            >
              <div className="hostel-fee-modal-dialog">
                <div className="hostel-fee-modal-content">
                  {/* =================================================
                HEADER
            ================================================= */}

                  <div className="hostel-fee-modal-header">
                    <div>
                      <h5 id="generateHostelFeeTitle" className="mb-1">
                        Generate Hostel Fee
                      </h5>

                      <small className="text-muted">
                        Select hostel and month to preview fee generation
                      </small>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeGenerateModal}
                      aria-label="Close"
                    />
                  </div>

                  {/* =================================================
                BODY
            ================================================= */}

                  <div className="hostel-fee-modal-body">
                    {/* HOSTEL + MONTH + PREVIEW */}

                    <div className="row gy-3 align-items-end mb-4">
                      {/* HOSTEL */}

                      <div className="col-md-5">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={genHostelId}
                            onChange={(e) => setGenHostelId(e.target.value)}
                          >
                            <option value="">Select Hostel</option>

                            {hostels?.data?.map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.name}
                              </option>
                            ))}
                          </select>

                          <label>Hostel</label>
                        </div>
                      </div>

                      {/* MONTH */}

                      <div className="col-md-4">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="month"
                            className="form-control"
                            value={genMonth}
                            onChange={(e) => setGenMonth(e.target.value)}
                          />

                          <label>Month</label>
                        </div>
                      </div>

                      {/* PREVIEW */}

                      <div className="col-md-3">
                        <button
                          type="button"
                          className="btn btn-outline-primary w-100"
                          onClick={fetchPreview}
                          disabled={previewLoading}
                        >
                          {previewLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              Loading...
                            </>
                          ) : (
                            <>
                              <i className="ri-search-line me-1" />
                              Preview
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* =================================================
                  PREVIEW RESULT
              ================================================= */}

                    {previewData && (
                      <>
                        {/* SUMMARY BADGES */}

                        <div className="hostel-fee-preview-summary">
                          <div className="hostel-fee-summary-item">
                            <span className="text-muted">Hostel</span>

                            <strong>{previewData.hostel_name}</strong>
                          </div>

                          <div className="hostel-fee-summary-item">
                            <span className="text-muted">Month</span>

                            <strong>{previewData.month}</strong>
                          </div>

                          <div className="hostel-fee-summary-item">
                            <span className="text-muted">To Generate</span>

                            <strong className="text-success">
                              {previewData.pending_count}
                            </strong>
                          </div>

                          {previewData.already_generated?.length > 0 && (
                            <div className="hostel-fee-summary-item">
                              <span className="text-muted">
                                Already Generated
                              </span>

                              <strong>
                                {previewData.already_generated.length}
                              </strong>
                            </div>
                          )}

                          {previewData.not_applicable?.length > 0 && (
                            <div className="hostel-fee-summary-item">
                              <span className="text-muted">Not Joined</span>

                              <strong>
                                {previewData.not_applicable.length}
                              </strong>
                            </div>
                          )}
                        </div>

                        {/* =================================================
                      NO RECORDS
                  ================================================= */}

                        {rows.length === 0 ? (
                          <div className="hostel-fee-empty-state">
                            <div className="hostel-fee-empty-icon">
                              <i className="ri-checkbox-circle-line ri-28px" />
                            </div>

                            <h6>Nothing Pending</h6>

                            <p className="text-muted mb-0">
                              Every active student's fee for this month is
                              already generated.
                            </p>
                          </div>
                        ) : (
                          /* =================================================
                       STUDENT TABLE
                    ================================================= */

                          <div className="hostel-fee-table-wrapper">
                            <table className="table align-middle mb-0">
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      width: "50px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={allChecked}
                                      onChange={(e) =>
                                        toggleAllChecked(e.target.checked)
                                      }
                                    />
                                  </th>

                                  <th>Student</th>

                                  <th>Reg No</th>

                                  <th
                                    className="text-end"
                                    style={{
                                      width: "170px",
                                    }}
                                  >
                                    Amount
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {rows.map((row) => (
                                  <tr key={row.hostel_student_id}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={row.checked}
                                        onChange={() =>
                                          toggleRowChecked(
                                            row.hostel_student_id,
                                          )
                                        }
                                      />
                                    </td>

                                    <td>
                                      <div className="fw-medium">
                                        {row.student_name}
                                      </div>

                                      {row.is_prorated && (
                                        <span className="badge bg-label-info mt-1">
                                          Prorated
                                        </span>
                                      )}
                                    </td>

                                    <td>{row.registration_no}</td>

                                    <td>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm text-end"
                                        value={row.amount}
                                        disabled={!row.checked}
                                        min="0"
                                        step="0.01"
                                        onChange={(e) =>
                                          updateRowAmount(
                                            row.hostel_student_id,
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* =================================================
                FOOTER
            ================================================= */}

                  <div className="hostel-fee-modal-footer">
                    <div className="me-auto">
                      {previewData && rows.length > 0 && (
                        <span className="text-muted">
                          <strong>{checkedCount}</strong> student
                          {checkedCount !== 1 ? "s" : ""} selected
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      className="btn btn-label-secondary"
                      onClick={closeGenerateModal}
                      disabled={generating}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={submitGenerate}
                      disabled={generating || checkedCount === 0}
                    >
                      {generating ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          Generating...
                        </>
                      ) : (
                        <>
                          <i className="ri-add-circle-line me-1" />
                          Generate Fee ({checkedCount})
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* =========================================================
          MODAL CSS
      ========================================================= */}

            <style>
              {`
          .hostel-fee-modal-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(30, 30, 45, 0.55);
            z-index: 2000;
            backdrop-filter: blur(2px);
          }

          .hostel-fee-modal-wrapper {
            position: fixed;
            inset: 0;
            z-index: 2001;

            display: flex;
            align-items: center;
            justify-content: center;

            padding: 24px;

            pointer-events: none;
          }

          .hostel-fee-modal-dialog {
            width: 100%;
            max-width: 1000px;

            max-height: calc(100vh - 48px);

            pointer-events: auto;
          }

          .hostel-fee-modal-content {
            display: flex;
            flex-direction: column;

            width: 100%;
            max-height: calc(100vh - 48px);

            background: var(--bs-body-bg, #fff);

            border-radius: 16px;

            box-shadow:
              0 20px 60px rgba(0, 0, 0, 0.25);

            overflow: hidden;
          }

          .hostel-fee-modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;

            padding: 20px 24px;

            border-bottom:
              1px solid var(--bs-border-color, #e7e7e7);

            flex-shrink: 0;
          }

          .hostel-fee-modal-header h5 {
            font-size: 18px;
            font-weight: 600;
          }

          .hostel-fee-modal-body {
            padding: 24px;

            overflow-y: auto;

            flex: 1;
            min-height: 0;
          }

          .hostel-fee-modal-footer {
            display: flex;
            align-items: center;
            gap: 10px;

            padding: 16px 24px;

            border-top:
              1px solid var(--bs-border-color, #e7e7e7);

            flex-shrink: 0;
          }

          .hostel-fee-preview-summary {
            display: grid;

            grid-template-columns:
              repeat(auto-fit, minmax(150px, 1fr));

            gap: 12px;

            margin-bottom: 20px;
          }

          .hostel-fee-summary-item {
            display: flex;
            flex-direction: column;

            gap: 4px;

            padding: 12px 14px;

            border-radius: 10px;

            background:
              rgba(105, 108, 255, 0.06);

            border:
              1px solid rgba(105, 108, 255, 0.10);
          }

          .hostel-fee-summary-item span {
            font-size: 12px;
          }

          .hostel-fee-summary-item strong {
            font-size: 14px;
          }

          .hostel-fee-table-wrapper {
            border:
              1px solid var(--bs-border-color, #e7e7e7);

            border-radius: 10px;

            overflow: auto;

            max-height: 390px;
          }

          .hostel-fee-table-wrapper thead {
            position: sticky;
            top: 0;

            z-index: 2;

            background:
              var(--bs-tertiary-bg, #f8f8fa);
          }

          .hostel-fee-table-wrapper th {
            font-size: 12px;

            text-transform: uppercase;

            letter-spacing: 0.3px;

            white-space: nowrap;
          }

          .hostel-fee-table-wrapper td {
            padding-top: 12px;
            padding-bottom: 12px;
          }

          .hostel-fee-empty-state {
            display: flex;
            flex-direction: column;

            align-items: center;
            justify-content: center;

            min-height: 220px;

            text-align: center;

            padding: 30px;
          }

          .hostel-fee-empty-icon {
            display: flex;

            align-items: center;
            justify-content: center;

            width: 60px;
            height: 60px;

            margin-bottom: 14px;

            border-radius: 50%;

            background:
              rgba(40, 199, 111, 0.10);

            color:
              var(--bs-success, #28c76f);
          }

          @media (max-width: 768px) {

            .hostel-fee-modal-wrapper {
              padding: 12px;
            }

            .hostel-fee-modal-content {
              max-height:
                calc(100vh - 24px);

              border-radius: 12px;
            }

            .hostel-fee-modal-header {
              padding: 16px;
            }

            .hostel-fee-modal-body {
              padding: 16px;
            }

            .hostel-fee-modal-footer {
              padding: 12px 16px;

              flex-wrap: wrap;
            }

            .hostel-fee-modal-footer .me-auto {
              width: 100%;
            }

          }
        `}
            </style>
          </>,
          document.body,
        )}

      {/* =========================================================
    HOSTEL FEE RECEIPT MODAL
========================================================= */}

      {showReceiptModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content border-0 shadow-lg">
              {/* =====================================================
            MODAL HEADER
        ===================================================== */}

              <div className="modal-header">
                <div>
                  <h5 className="modal-title fw-bold mb-1">
                    <i className="ri-receipt-line me-2 text-primary" />
                    Hostel Fee Receipt
                  </h5>

                  <small className="text-muted">
                    Preview and print hostel fee receipt
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeReceiptModal}
                />
              </div>

              {/* =====================================================
            MODAL BODY
        ===================================================== */}

              <div className="modal-body bg-light">
                {receiptLoading ? (
                  <div
                    className="d-flex flex-column justify-content-center align-items-center"
                    style={{
                      minHeight: 400,
                    }}
                  >
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>

                    <div className="text-muted">Loading receipt...</div>
                  </div>
                ) : (
                  <div className="d-flex justify-content-center">
                    <div
                      id="FeeReceiptPrintArea"
                      className="bg-white shadow-sm"
                      style={{
                        width: "100%",
                        maxWidth: "800px",
                        padding: "20px",
                      }}
                    >
                      <HostelFeeReceipt data={receiptData} />
                    </div>
                  </div>
                )}
              </div>

              {/* =====================================================
            MODAL FOOTER
        ===================================================== */}

              {!receiptLoading && (
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-label-secondary"
                    onClick={closeReceiptModal}
                  >
                    <i className="ri-close-line me-1" />
                    Close
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => printElement("FeeReceiptPrintArea")}
                  >
                    <i className="ri-printer-line me-1" />
                    Print Receipt
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
    RECEIPT MODAL BACKDROP
========================================================= */}

      {showReceiptModal && (
        <div className="modal-backdrop fade show" onClick={closeReceiptModal} />
      )}
    </>
  );
}
